import { useCallback, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons"
import api from "../../components/utils/api"
import ButtonWrap from "../../components/template/ButtonWrap"
import TrainingProgressView from "./TrainingProgressView"

// Progress of one training run, looked up by its exact result-folder name.
const ModelTrainingProgressPage = () => {
    const params = useParams()
    const navigate = useNavigate()
    const [progress, setProgress] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState(null)
    const [isResolving, setIsResolving] = useState(false)
    const [resolveError, setResolveError] = useState(null)

    const fetchProgress = useCallback(async () => {
        setIsLoading(true)
        setError(null)
        try {
            const res = await api.get(`/api/general-models/${encodeURIComponent(params.modelName)}/training-progress`)
            setProgress(res.data)
        } catch (err) {
            console.error(err)
            setError(err.response?.data?.message ?? 'Failed to fetch training progress')
        } finally {
            setIsLoading(false)
        }
    }, [params.modelName])

    useEffect(() => { fetchProgress() }, [fetchProgress])

    // Resolving materializes the general_models row on first call (GCS scan),
    // so it runs on click rather than on page load.
    const goToPostAction = async () => {
        setIsResolving(true)
        setResolveError(null)
        // The tab must be opened synchronously within the click gesture;
        // opening it after the await would be popup-blocked.
        const postActionTab = window.open('', '_blank')
        try {
            const res = await api.get(`/api/general-models/resolve?model_name=${encodeURIComponent(params.modelName)}`)
            const path = `/post-action/models/${res.data.id}`
            if (postActionTab) postActionTab.location.href = path
            else navigate(path)
        } catch (err) {
            console.error(err)
            postActionTab?.close()
            setResolveError(err.response?.status === 404
                ? 'Model results are not synced to GCS yet. Please try again later.'
                : (err.response?.data?.message ?? 'Failed to open the post action page'))
        } finally {
            setIsResolving(false)
        }
    }

    return <TrainingProgressView
        progress={progress}
        isLoading={isLoading}
        error={error ?? resolveError}
        onRefresh={fetchProgress}
        headerAction={progress?.status === 'completed' &&
            <ButtonWrap
                contents={isResolving ? "Loading..." : <>Post Action <FontAwesomeIcon icon={faArrowUpRightFromSquare} /></>}
                onClick={goToPostAction}
                isDisabled={isResolving}
            />}
    />
}

export default ModelTrainingProgressPage
